import React, { useState, useEffect, useMemo } from 'react';
import { FiRefreshCw, FiUser, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import { IconType } from 'react-icons';
import { dashboardService } from '../api/services/dashboardService';
import { ProfileTreeResponse, UserDetail, PaginatedResponse } from '../api/types';
import { getUserFriendlyErrorMessage } from '../api/utils/errorHandler';

const ProfileTree: React.FC = () => {
  const [playerId, setPlayerId] = useState<string>('');
  const [type, setType] = useState<'tests' | 'profile'>('tests');
  const [data, setData] = useState<ProfileTreeResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // User details list state
  const [users, setUsers] = useState<UserDetail[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [usersLoading, setUsersLoading] = useState<boolean>(true);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
  });
  const [userProfileTrees, setUserProfileTrees] = useState<Record<string, ProfileTreeResponse>>({});
  const [loadingProfileTrees, setLoadingProfileTrees] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<'numeric_id' | 'player_id' | 'country_code' | 'device_model' | 'test_session_count' | 'game_session_count' | 'chat_session_count'>('numeric_id');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const renderIcon = (Icon: IconType, className: string) => {
    const IconComponent = Icon as React.ComponentType<{ className?: string }>;
    return <IconComponent className={className} />;
  };

  const fetchProfileTree = async () => {
    if (!playerId.trim()) {
      setError('Lütfen bir Player ID girin');
      return;
    }

    setLoading(true);
    setError(null);
    setData(null);

    try {
      const response = await dashboardService.getProfileTree({
        player_id: playerId.trim(),
        type,
      });
      // API returns an array, get the first element if it exists
      const data = Array.isArray(response) ? (response[0] || {}) : response;
      setData(data as ProfileTreeResponse);
    } catch (err: any) {
      const errorMessage = getUserFriendlyErrorMessage(err);
      setError(errorMessage);
      console.error('Error fetching profile tree:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchProfileTree();
  };

  // Fetch user details list
  const fetchUserDetails = async (page: number = 1) => {
    try {
      setUsersLoading(true);
      setUsersError(null);

      const response: PaginatedResponse<UserDetail> = await dashboardService.getUserDetails({
        page,
      });

      setUsers(response.data);
      setPagination({
        total: response.pagination.total,
        totalPages: response.pagination.totalPages,
      });
    } catch (err: any) {
      const errorMessage = getUserFriendlyErrorMessage(err);
      setUsersError(errorMessage);
      console.error('Error fetching user details:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    fetchUserDetails(currentPage);
  }, [currentPage]);

  // Fetch profile tree for each user automatically
  useEffect(() => {
    const fetchProfileTreesForUsers = async () => {
      if (users.length === 0) return;

      // Fetch profile tree for each user (both tests and profile)
      for (const user of users) {
        // Skip if already loaded
        if (userProfileTrees[user.player_id] || loadingProfileTrees.has(user.player_id)) {
          continue;
        }

        try {
          setLoadingProfileTrees(prev => new Set(prev).add(user.player_id));
          
          // Fetch both tests and profile data
          const [testsResponse, profileResponse] = await Promise.all([
            dashboardService.getProfileTree({
              player_id: user.player_id,
              type: 'tests',
            }).catch(() => null),
            dashboardService.getProfileTree({
              player_id: user.player_id,
              type: 'profile',
            }).catch(() => null),
          ]);

          // API returns arrays, get first element if exists
          const testsData = Array.isArray(testsResponse) ? (testsResponse[0] || {}) : (testsResponse || {});
          const profileData = Array.isArray(profileResponse) ? (profileResponse[0] || {}) : (profileResponse || {});

          // Combine both types
          const combinedData: ProfileTreeResponse = {
            ...testsData,
            ...profileData,
          } as ProfileTreeResponse;

          setUserProfileTrees(prev => ({
            ...prev,
            [user.player_id]: combinedData,
          }));

          // Small delay to avoid overwhelming the API
          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (err) {
          console.error(`Error fetching profile tree for user ${user.player_id}:`, err);
          // Set empty data on error
          setUserProfileTrees(prev => ({
            ...prev,
            [user.player_id]: {},
          }));
        } finally {
          setLoadingProfileTrees(prev => {
            const newSet = new Set(prev);
            newSet.delete(user.player_id);
            return newSet;
          });
        }
      }
    };

    fetchProfileTreesForUsers();
  }, [users]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setCurrentPage(page);
    }
  };

  // Sort users data
  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'numeric_id':
          aValue = a.numeric_id;
          bValue = b.numeric_id;
          break;
        case 'player_id':
          aValue = a.player_id || '';
          bValue = b.player_id || '';
          break;
        case 'country_code':
          aValue = (a.country_code || '').toLowerCase();
          bValue = (b.country_code || '').toLowerCase();
          break;
        case 'device_model':
          aValue = (a.device_model || '').toLowerCase();
          bValue = (b.device_model || '').toLowerCase();
          break;
        case 'test_session_count':
          aValue = a.test_session_count || 0;
          bValue = b.test_session_count || 0;
          break;
        case 'game_session_count':
          aValue = a.game_session_count || 0;
          bValue = b.game_session_count || 0;
          break;
        case 'chat_session_count':
          aValue = a.chat_session_count || 0;
          bValue = b.chat_session_count || 0;
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'desc' ? bValue - aValue : aValue - bValue;
      } else {
        if (sortDirection === 'desc') {
          return bValue.localeCompare(aValue);
        } else {
          return aValue.localeCompare(bValue);
        }
      }
    });
  }, [users, sortField, sortDirection]);

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  return (
    <div className="w-full max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 dark:from-slate-200 dark:to-slate-400 bg-clip-text text-transparent mb-2">
          Profil Ağacı
        </h1>
        <p className="text-slate-600 dark:text-slate-400">
          Kullanıcı profil ağacı verilerini görüntüleyin
        </p>
      </div>

      {/* Search Form */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 p-6 border border-gray-100 dark:border-gray-700">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Player ID
              </label>
              <input
                type="text"
                value={playerId}
                onChange={(e) => setPlayerId(e.target.value)}
                placeholder="Örn: ab2df71c-f4ad-4ad5-9522-935ea2aea8cf"
                className="w-full px-4 py-2.5 bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-sm font-medium text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:focus:border-indigo-400 transition-all duration-200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Tip
              </label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as 'tests' | 'profile')}
                className="w-full px-4 py-2.5 bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-sm font-medium text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:focus:border-indigo-400 transition-all duration-200"
              >
                <option value="tests">Tests</option>
                <option value="profile">Profile</option>
              </select>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 dark:bg-indigo-500 text-white rounded-xl hover:bg-indigo-700 dark:hover:bg-indigo-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {renderIcon(FiRefreshCw, `w-4 h-4 ${loading ? 'animate-spin' : ''}`)}
            <span>{loading ? 'Yükleniyor...' : 'Ara'}</span>
          </button>
        </form>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
            <span>⚠️</span>
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Results */}
      {data && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-slate-800 dark:text-white">
              {type === 'tests' ? 'Test Sonuçları' : 'Profil Verileri'}
            </h2>
            <span className="text-sm text-slate-600 dark:text-slate-400">
              Player ID: {playerId}
            </span>
          </div>

          {Object.keys(data).length === 0 ? (
            <div className="text-center py-12 text-slate-500 dark:text-slate-400 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700">
              Veri bulunamadı
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Object.entries(data).map(([key, value]) => {
                // Handle different value types
                let displayValue: string;
                if (value === null || value === undefined) {
                  displayValue = '-';
                } else if (Array.isArray(value)) {
                  displayValue = value.length > 0 ? value.join(', ') : '-';
                } else if (typeof value === 'object') {
                  displayValue = JSON.stringify(value);
                } else {
                  displayValue = String(value);
                }

                return (
                  <div
                    key={key}
                    className="bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 p-4 border border-gray-200 dark:border-gray-700"
                  >
                    <div className="mb-2">
                      <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                        {key}
                      </span>
                    </div>
                    <div className="break-words">
                      <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
                        {displayValue.length > 100 ? `${displayValue.substring(0, 100)}...` : displayValue}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* User Details List */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100 dark:border-gray-700">
        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-800">
          <h2 className="text-xl font-semibold text-slate-800 dark:text-white">Kullanıcı Listesi</h2>
        </div>
        {usersError && (
          <div className="px-6 py-4 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
            <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
              <span>⚠️</span>
              <span>{usersError}</span>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          {usersLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gradient-to-r from-slate-50 to-gray-50 dark:from-gray-700 dark:to-gray-800">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 dark:hover:from-indigo-900/20 dark:hover:to-purple-900/20 transition-all duration-200 select-none"
                    onClick={() => handleSort('numeric_id')}
                  >
                    <div className="flex items-center gap-2">
                      <span>ID</span>
                      {sortField === 'numeric_id' ? (
                        renderIcon(sortDirection === 'desc' ? FiArrowDown : FiArrowUp, "w-4 h-4 text-indigo-600 dark:text-indigo-400")
                      ) : (
                        <span className="w-4 h-4 opacity-30">
                          {renderIcon(FiArrowDown, "w-4 h-4 text-slate-400")}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 dark:hover:from-indigo-900/20 dark:hover:to-purple-900/20 transition-all duration-200 select-none"
                    onClick={() => handleSort('player_id')}
                  >
                    <div className="flex items-center gap-2">
                      <span>Player ID</span>
                      {sortField === 'player_id' ? (
                        renderIcon(sortDirection === 'desc' ? FiArrowDown : FiArrowUp, "w-4 h-4 text-indigo-600 dark:text-indigo-400")
                      ) : (
                        <span className="w-4 h-4 opacity-30">
                          {renderIcon(FiArrowDown, "w-4 h-4 text-slate-400")}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 dark:hover:from-indigo-900/20 dark:hover:to-purple-900/20 transition-all duration-200 select-none"
                    onClick={() => handleSort('country_code')}
                  >
                    <div className="flex items-center gap-2">
                      <span>Ülke</span>
                      {sortField === 'country_code' ? (
                        renderIcon(sortDirection === 'desc' ? FiArrowDown : FiArrowUp, "w-4 h-4 text-indigo-600 dark:text-indigo-400")
                      ) : (
                        <span className="w-4 h-4 opacity-30">
                          {renderIcon(FiArrowDown, "w-4 h-4 text-slate-400")}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 dark:hover:from-indigo-900/20 dark:hover:to-purple-900/20 transition-all duration-200 select-none"
                    onClick={() => handleSort('device_model')}
                  >
                    <div className="flex items-center gap-2">
                      <span>Cihaz</span>
                      {sortField === 'device_model' ? (
                        renderIcon(sortDirection === 'desc' ? FiArrowDown : FiArrowUp, "w-4 h-4 text-indigo-600 dark:text-indigo-400")
                      ) : (
                        <span className="w-4 h-4 opacity-30">
                          {renderIcon(FiArrowDown, "w-4 h-4 text-slate-400")}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 dark:hover:from-indigo-900/20 dark:hover:to-purple-900/20 transition-all duration-200 select-none"
                    onClick={() => handleSort('test_session_count')}
                  >
                    <div className="flex items-center gap-2">
                      <span>Test</span>
                      {sortField === 'test_session_count' ? (
                        renderIcon(sortDirection === 'desc' ? FiArrowDown : FiArrowUp, "w-4 h-4 text-indigo-600 dark:text-indigo-400")
                      ) : (
                        <span className="w-4 h-4 opacity-30">
                          {renderIcon(FiArrowDown, "w-4 h-4 text-slate-400")}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 dark:hover:from-indigo-900/20 dark:hover:to-purple-900/20 transition-all duration-200 select-none"
                    onClick={() => handleSort('game_session_count')}
                  >
                    <div className="flex items-center gap-2">
                      <span>Oyun</span>
                      {sortField === 'game_session_count' ? (
                        renderIcon(sortDirection === 'desc' ? FiArrowDown : FiArrowUp, "w-4 h-4 text-indigo-600 dark:text-indigo-400")
                      ) : (
                        <span className="w-4 h-4 opacity-30">
                          {renderIcon(FiArrowDown, "w-4 h-4 text-slate-400")}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 dark:hover:from-indigo-900/20 dark:hover:to-purple-900/20 transition-all duration-200 select-none"
                    onClick={() => handleSort('chat_session_count')}
                  >
                    <div className="flex items-center gap-2">
                      <span>Sohbet</span>
                      {sortField === 'chat_session_count' ? (
                        renderIcon(sortDirection === 'desc' ? FiArrowDown : FiArrowUp, "w-4 h-4 text-indigo-600 dark:text-indigo-400")
                      ) : (
                        <span className="w-4 h-4 opacity-30">
                          {renderIcon(FiArrowDown, "w-4 h-4 text-slate-400")}
                        </span>
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                {sortedUsers.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-slate-500 dark:text-slate-400">
                      Kullanıcı bulunamadı
                    </td>
                  </tr>
                ) : (
                  sortedUsers.map((user) => {
                    const profileTree = userProfileTrees[user.player_id];
                    const isLoadingTree = loadingProfileTrees.has(user.player_id);
                    
                    return (
                      <React.Fragment key={user.numeric_id}>
                        <tr className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900 dark:text-white">{user.numeric_id}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-slate-600 dark:text-slate-300">{user.player_id}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">{user.country_code || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                            <div className="flex flex-col">
                              <span>{user.device_model || '-'}</span>
                              <span className="text-xs text-slate-400">{user.screen_resolution || '-'}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">{user.test_session_count || 0}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">{user.game_session_count || 0}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">{user.chat_session_count || 0}</td>
                        </tr>
                        {/* Profile Tree Data Row */}
                        {(isLoadingTree || profileTree) && (
                          <tr>
                            <td colSpan={7} className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50">
                              {isLoadingTree ? (
                                <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                                  <span>Profil ağacı yükleniyor...</span>
                                </div>
                              ) : profileTree && Object.keys(profileTree).length > 0 ? (
                                <div className="space-y-2">
                                  <h4 className="text-sm font-semibold text-slate-800 dark:text-white mb-2">Profil Ağacı Verileri</h4>
                                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                    {Object.entries(profileTree).map(([key, value]) => {
                                      // Handle different value types
                                      let displayValue: string;
                                      if (value === null || value === undefined) {
                                        displayValue = '-';
                                      } else if (Array.isArray(value)) {
                                        displayValue = value.length > 0 ? value.join(', ') : '-';
                                      } else if (typeof value === 'object') {
                                        displayValue = JSON.stringify(value);
                                      } else {
                                        displayValue = String(value);
                                      }

                                      return (
                                        <div
                                          key={key}
                                          className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700"
                                        >
                                          <span className="text-xs font-medium text-slate-600 dark:text-slate-400">{key}:</span>
                                          <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 ml-2">{displayValue}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              ) : null}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </div>
        
        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="text-sm text-slate-600 dark:text-slate-400">
              Toplam {pagination.total} kayıttan sayfa {currentPage} / {pagination.totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || usersLoading}
                className="px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Önceki
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      disabled={usersLoading}
                      className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        currentPage === pageNum
                          ? 'bg-indigo-600 dark:bg-indigo-500 text-white'
                          : 'text-slate-700 dark:text-slate-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === pagination.totalPages || usersLoading}
                className="px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Sonraki
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileTree;
